import type { ComponentConfig, Config } from "@puckeditor/core";
import type { FieldDef, FieldType } from "./form-data";
import { useFormUpload, useFormValue } from "./form-values-context";
import { CORE_FIELD_COMPONENTS } from "./form-data";
import { FieldInput } from "./field-input";
import { LinksPageConfig } from "../components/links-page/links-page.config";
import type { LinksPageProps } from "../components/links-page/links-page";

/**
 * The forms Puck config — one component per field type. Puck `<Render>` (and the
 * Puck editor preview) draw these; each `render` binds the shared FieldInput to
 * the form-values context by the component's `id` (which is the formData.custom
 * key). This is the single field renderer for both the editor and the live form.
 */

interface BaseFieldProps {
  id?: string; // Puck-managed instance id; the formData.custom key
  label: string;
  required?: string; // "yes" | "no"
  helpText?: string;
}
interface OptionFieldProps extends BaseFieldProps {
  options?: { label: string; value: string }[];
}

const FieldBinding = ({ field }: { field: FieldDef }) => {
  const [value, setValue] = useFormValue(field.id);
  const uploadImage = useFormUpload();
  // Vertical rhythm between fields — Puck stacks components flush otherwise.
  // Applies everywhere the config renders: editor canvas, preview, live widget.
  return (
    <div className="jobsite:py-2">
      <FieldInput field={field} value={value} onChange={setValue} uploadImage={uploadImage} />
    </div>
  );
};

const baseFields = {
    label: { type: "text" as const, label: "Label" },
    required: {
      type: "radio" as const,
      label: "Required",
      options: [
        { label: "Required", value: "yes" },
        { label: "Optional", value: "no" },
      ],
    },
    helpText: { type: "text" as const, label: "Help text" },
  },

  optionField = (
    type: FieldType,
    label: string,
    defaultLabel: string,
  ): ComponentConfig<OptionFieldProps> => ({
    defaultProps: { label: defaultLabel, options: [], required: "no" },
    fields: {
      ...baseFields,
      options: {
        arrayFields: {
          label: { type: "text" as const },
          value: { type: "text" as const },
        },
        defaultItemProps: { label: "Option", value: "option" },
        label: "Options",
        type: "array" as const,
      },
    },
    label,
    render: ({ id, label, required, helpText, options }) => (
      <FieldBinding
        field={{ helpText, id: id ?? "", label, options, required: required === "yes", type }}
      />
    ),
  }),

  simpleField = (
    type: FieldType,
    label: string,
    defaultLabel: string,
  ): ComponentConfig<BaseFieldProps> => ({
    defaultProps: { label: defaultLabel, required: "no" },
    fields: { ...baseFields },
    label,
    render: ({ id, label, required, helpText }) => (
      <FieldBinding
        field={{ helpText, id: id ?? "", label, required: required === "yes", type }}
      />
    ),
  });

/**
 * A core contact field (see CORE_FIELD_COMPONENTS): renders like any field, but
 * binds to its canonical top-level formData key instead of the Puck instance id
 * — so dedup/notifications/workflows read the same keys they always have.
 */
const coreField = (
  componentType: string,
  paletteLabel: string,
  defaultLabel: string,
): ComponentConfig<BaseFieldProps> => {
  const spec = CORE_FIELD_COMPONENTS[componentType];
  return {
    defaultProps: { label: defaultLabel, required: "no" },
    fields: { ...baseFields },
    label: paletteLabel,
    render: ({ label, required, helpText }) => (
      <FieldBinding
        field={{
          autoComplete: spec.autoComplete,
          helpText,
          id: spec.key,
          label,
          required: required === "yes",
          type: spec.type,
        }}
      />
    ),
  };
};

interface FormFieldComponentProps {
  TextField: BaseFieldProps;
  TextareaField: BaseFieldProps;
  NumberField: BaseFieldProps;
  DateField: BaseFieldProps;
  BooleanField: BaseFieldProps;
  ImageField: BaseFieldProps;
  SelectField: OptionFieldProps;
  MultiSelectField: OptionFieldProps;
}

interface ContactFormComponentProps extends FormFieldComponentProps {
  NameField: BaseFieldProps;
  EmailField: BaseFieldProps;
  PhoneField: BaseFieldProps;
  AddressField: BaseFieldProps;
  MessageField: BaseFieldProps;
}

/** The contact form has no form-level settings (the booking-only root knobs —
 *  note, "Additional details", step text — don't apply). */
interface ContactFormRootProps {
  [key: string]: never;
}

/** Form-level ("Page") settings — built-in knobs that aren't per-field. Read by
 *  the booking surfaces via the helpers in form-data.ts. */
interface FormRootProps {
  noteEnabled?: string; // "yes" | "no" — the "Anything else?" note
  noteLabel?: string;
  noteRequired?: string; // "yes" | "no"
  additionalDetails?: { text: string }[]; // Booking-page "Additional details" bullets
  additionalDetailsHeading?: string; // Heading over those bullets (default "Additional details")
  /** @deprecated Pre-rename keys (the section was mocked up as "Good to know").
   *  Read-only back-compat: the read helpers fall back to these for booking forms
   *  persisted before the rename; new writes use additionalDetails*. */
  goodToKnow?: { text: string }[];
  goodToKnowHeading?: string;
  heroImageUrl?: string; // Booking-page hero card image (overrides the GBP photo)
}

// <Puck>/<Render> accept the base `Config`, but a parameterized
// `Config<Props, Root>` doesn't structurally satisfy that constraint in Puck's
// Types. This boundary keeps full per-component type-checking on the literal
// (its argument is typed) while handing callers the base `Config` they accept —
// The one and only widening lives here, not scattered across call sites.
//
// Tracking puckeditor/puck#1519 (Puck's generic config/Fields type-distribution
// Bug). Revisit and drop this boundary once a Puck release lets a parameterized
// `Config<Props, Root>` satisfy the base `Config` constraint directly.
// oxlint-disable typescript/unified-signatures -- one union parameter loses each config's contextual type for render({ children })
function asPuckConfig(config: Config<FormFieldComponentProps, FormRootProps>): Config;
function asPuckConfig(config: Config<ContactFormComponentProps, ContactFormRootProps>): Config;
function asPuckConfig(config: Config<{ LinksPage: LinksPageProps }>): Config;
// oxlint-enable typescript/unified-signatures
function asPuckConfig(config: unknown): Config {
  return config as Config;
}

/** The custom-field palette, shared by both configs below. */
const fieldComponents: Config<FormFieldComponentProps>["components"] = {
  TextField: simpleField("text", "Text", "Text field"),
  TextareaField: simpleField("textarea", "Long text", "Message"),
  NumberField: simpleField("number", "Number", "Number"),
  DateField: simpleField("date", "Date", "Date"),
  BooleanField: simpleField("boolean", "Yes / no", "I agree"),
  ImageField: simpleField("image", "Photo upload", "Upload a photo"),
  SelectField: optionField("select", "Dropdown", "Choose one"),
  MultiSelectField: optionField("multiselect", "Multi-select", "Select all that apply"),
};

const formsPuckConfig: Config = asPuckConfig({
  components: fieldComponents,
  root: {
    defaultProps: {
      additionalDetails: [
        { text: "No account or app to download. Just verify by text and you're booked." },
        { text: "See pricing up front, or get a price at the visit before any work begins." },
      ],
      heroImageUrl: "",
      noteEnabled: "no",
      noteLabel: "Anything we should know?",
      noteRequired: "no",
    },
    fields: {
      noteEnabled: {
        label: "“Anything else?” note",
        options: [
          { label: "Show", value: "yes" },
          { label: "Hide", value: "no" },
        ],
        type: "radio",
      },
      noteLabel: { label: "Note label", type: "text" },
      noteRequired: {
        label: "Note required?",
        options: [
          { label: "Optional", value: "no" },
          { label: "Required", value: "yes" },
        ],
        type: "radio",
      },
      additionalDetails: {
        arrayFields: { text: { label: "Bullet", type: "textarea" } },
        defaultItemProps: { text: "" },
        label: "“Additional details” bullets",
        type: "array",
      },
    },
    // The form is just its fields — root only carries page-level settings.
    render: ({ children }) => <>{children}</>,
  },
});

/**
 * The contact-form Puck config: the same custom-field palette PLUS the core
 * contact fields (Name/Email/Phone/Message → canonical top-level formData
 * keys). A separate config on purpose: the booking builder must never offer
 * core fields — booking identity comes from its OTP flow, and a core key would
 * be dropped by the customFields filter on submit. Booking-only root settings
 * ("Additional details", step text, the note) don't apply here either.
 */
/**
 * The link-page Puck config: one component, whose fields ARE the whole form
 * (wiki/decisions/link-page-form.md). No palette, no core fields — the
 * builder locks structure and the panel edits the LinksPage node.
 */
const linksPuckConfig: Config = asPuckConfig({
  components: {
    LinksPage: LinksPageConfig,
  },
  root: {
    render: ({ children }) => <>{children}</>,
  },
});

const contactFormsPuckConfig: Config = asPuckConfig({
  components: {
    // Core fields first so the palette leads with them.
    NameField: coreField("NameField", "Name", "Your Name"),
    EmailField: coreField("EmailField", "Email", "Email Address"),
    PhoneField: coreField("PhoneField", "Phone", "Phone Number"),
    AddressField: coreField("AddressField", "Address", "Service Address"),
    MessageField: coreField("MessageField", "Message", "Your Message"),
    ...fieldComponents,
  },
  root: {
    render: ({ children }) => <>{children}</>,
  },
});

export type {
  FormFieldComponentProps,
  ContactFormComponentProps,
  ContactFormRootProps,
  FormRootProps,
};
export { formsPuckConfig, linksPuckConfig, contactFormsPuckConfig };
